import { t } from 'i18next';

import { generalModalActions } from '#/store/modals';

import { MoreItem, SettingItemType, SettingsItem } from '../config';
import { SettingItemTypes } from '../constants';

const KEY_PREFIX = 'settings.';

// this should be in sync with the settings keys defined in the config
export enum KnownSettings {
  Ccpa = 'ccpa',
  AutoPlay = 'autoPlay',
}
type SettingsKey = KnownSettings | string;

const serializableItemTypes: string[] = [
  SettingItemTypes.SELECT,
  SettingItemTypes.CHECKBOX,
];

export const initialiseDefaultSettings = (more: MoreItem[]) => {
  const moreItems = more.flatMap((moreItem) => moreItem.items || []);
  const selectionListItems = moreItems.flatMap((i) => i.items || []);

  const items = [...moreItems, ...selectionListItems];
  const serializableItems = items.filter(
    (i) => serializableItemTypes.indexOf(i.type) > -1
  );

  initSettingValues(serializableItems);
};

export const saveSetting = (
  key: SettingsKey,
  value: string,
  isInitial = false
) => {
  localStorage.setItem(KEY_PREFIX + key, value);

  // only show reload warning modal if CCPA setting has changed
  if (key === KnownSettings.Ccpa && !isInitial) {
    generalModalActions.showModal({
      message: t('settings.privacy.disclaimer'),
      buttonLabel: t('button.reload'),
      onOk: () => window.location.reload(),
    });
  }
};

export const loadSetting = (key: SettingsKey, defaultValue?: string) => {
  const storedValue = localStorage.getItem(KEY_PREFIX + key);

  return storedValue || defaultValue || null;
};

function initSettingValues(items: (SettingItemType | SettingsItem)[]) {
  items.forEach(({ key, default: defaultValue }) => {
    if (!key) {
      return;
    }
    const storedValue = loadSetting(key);

    if (key === KnownSettings.Ccpa) {
      // fix old CCPA values
      if (storedValue === 'DONTSELL') {
        saveSetting(KnownSettings.Ccpa, 'true', true);
      } else if (storedValue === 'SELL') {
        saveSetting(KnownSettings.Ccpa, 'false', true);
      }

      // early return, to avoid storing a default for CCPA, because we have to know that the uses has never set it
      return;
    }

    // store default value
    if (storedValue === null && defaultValue) {
      saveSetting(key, defaultValue.toString(), true);
      console.debug(`Settings default: ${key} = ${defaultValue}`);
    }
  });
}

// load CCPA opt out setting and convert to undefined/true/false
export const getCcpaOutOutSetting = (): undefined | boolean => {
  const stored = loadSetting(KnownSettings.Ccpa);

  if (stored === null) {
    return undefined;
  }

  return stored === 'true';
};
